import * as React from "react";
import { useNavigate } from "react-router";
import type { Driver } from "driver.js";
import { apiClient } from "@/api/client";
import type { OnboardingPersona } from "@/api/onboarding";
import { makeDriver } from "./driver-config";
import { PERSONA_TOURS } from "./scripts";

/**
 * Imperative product-tour controller.
 *
 * Why imperative: driver.js owns its own DOM (overlay + popover) so a
 * declarative React mount would just wrap a stateful imperative
 * library. Exposing ``start()`` / ``stop()`` from a hook is the
 * cleanest path — components call these in onClick handlers.
 *
 * Resume + completion:
 *  * ``start()`` honors the optional ``resumeStep`` map so a user who
 *    closed mid-tour picks up where they left off (spec 23 §5.1).
 *  * Every step advance fires PUT /me/tour-state best-effort. Failure
 *    is silent — tour state is convenience, not correctness.
 *  * The final next-click sets ``completed=true``; the next ``start()``
 *    of the same tour resets to step 0 (the saved last_step is past
 *    the end, so safeStart wraps).
 *
 * Routing: stops can carry a ``route`` field. ``onHighlightStarted``
 * pushes the route before driver.js paints the popover, so the
 * destination has a tick to render.
 */
export interface UseProductTourReturn {
  start: (tourId: OnboardingPersona, opts?: { resume?: boolean }) => void;
  stop: () => void;
  isRunning: boolean;
}

export interface UseProductTourOptions {
  /** Map of tourId → resume step. Wire from `useTourStates()` rows. */
  resumeStep?: Partial<Record<OnboardingPersona, number>>;
}

export function useProductTour(
  options: UseProductTourOptions = {},
): UseProductTourReturn {
  const navigate = useNavigate();
  const driverRef = React.useRef<Driver | null>(null);
  const tourIdRef = React.useRef<OnboardingPersona | null>(null);
  const [isRunning, setIsRunning] = React.useState(false);

  // Best-effort PUT to /me/tour-state. Don't await — we don't want to
  // gate the spotlight advance on the network round-trip.
  const persistStep = React.useCallback(
    (tourId: OnboardingPersona, lastStep: number, completed: boolean) => {
      apiClient
        .put(`/me/tour-state/${tourId}`, { last_step: lastStep, completed })
        .catch(() => undefined);
    },
    [],
  );

  const stop = React.useCallback(() => {
    driverRef.current?.destroy();
    driverRef.current = null;
    tourIdRef.current = null;
    setIsRunning(false);
  }, []);

  const start = React.useCallback(
    (tourId: OnboardingPersona, opts?: { resume?: boolean }) => {
      const stops = PERSONA_TOURS[tourId];
      if (!stops || stops.length === 0) return;

      const requestedStart =
        opts?.resume === false ? 0 : (options.resumeStep?.[tourId] ?? 0);
      // Past-the-end (previously completed) → restart at 0.
      const safeStart =
        requestedStart >= stops.length ? 0 : Math.max(0, requestedStart);

      const driverSteps = stops.map((stopDef, idx) => ({
        element: stopDef.selector ?? undefined,
        popover: {
          title: stopDef.title,
          description: stopDef.description,
          side: stopDef.side,
          onNextClick: () => {
            const nextIdx = idx + 1;
            const completed = idx === stops.length - 1;
            persistStep(tourId, nextIdx, completed);
            driverRef.current?.moveNext();
          },
          onPrevClick: () => {
            driverRef.current?.movePrevious();
          },
          onCloseClick: () => {
            persistStep(tourId, idx, false);
            stop();
          },
        },
        onHighlightStarted: () => {
          if (stopDef.route) navigate(stopDef.route);
        },
      }));

      const d = makeDriver({
        steps: driverSteps,
        onDestroyed: () => {
          driverRef.current = null;
          tourIdRef.current = null;
          setIsRunning(false);
        },
      });
      driverRef.current = d;
      tourIdRef.current = tourId;
      setIsRunning(true);
      d.drive(safeStart);
    },
    [navigate, options.resumeStep, persistStep, stop],
  );

  // Cleanup on unmount — driver.js leaks its overlay otherwise.
  React.useEffect(() => {
    return () => {
      driverRef.current?.destroy();
      driverRef.current = null;
    };
  }, []);

  return { start, stop, isRunning };
}
